import type { ManagementLevel, OrgMember, OrgRole } from '@/services/org-admin';
import { grantableRolesFor } from './roleOptions';

/**
 * Who can be moved onto a given role, and whether the actor may do it at all.
 *
 * Pure, and separate from the dialog, because every rule here is a server-side constraint that the
 * UI mirrors — and a mirror that drifts is worse than no mirror. `updateOrgUserRole` rejects a
 * non-active member and a role from another org, and the backend's `authorize_level_grant` rejects
 * an admin granting authority above their own. Each of those would surface as a failed mutation with
 * a message that reads like a bug, so the control is gated instead.
 */

/** Whether this actor may grant this specific role. Mirrors `grantableRolesFor` for one role. */
export function canGrantRole(
  currentUserRole: ManagementLevel | null,
  role: OrgRole,
): boolean {
  return grantableRolesFor(currentUserRole, [role]).length > 0;
}

/**
 * Why the actor cannot assign members to this role, or `undefined` when they can.
 *
 * Returns the reason rather than a boolean so the disabled control can explain itself — a greyed-out
 * button with no tooltip is the thing this module's `IconActionButton` exists to prevent.
 */
export function assignBlockedReason(
  currentUserRole: ManagementLevel | null,
  role: OrgRole,
  candidateCount: number,
): string | undefined {
  if (!canGrantRole(currentUserRole, role)) {
    return role.management_level === 'owner'
      ? 'Only an owner can assign an owner-level role'
      : 'Only an owner can assign a role that carries admin authority';
  }
  if (candidateCount === 0) return 'Every active member already holds this role';
  return undefined;
}

/**
 * Active members who do not already hold `role`.
 *
 * Two exclusions, both load-bearing:
 *
 *   - **Non-active members.** The mutation requires an active member and 403s otherwise. Offering a
 *     deactivated seat here would produce a failure the admin cannot act on without first
 *     reactivating them, which this dialog cannot do.
 *   - **Members already on the role.** Assigning someone the role they already hold is a no-op
 *     request. Worse, it pads the "3 of 4 assigned" summary with work that never needed doing.
 *
 * Sorted by email so the list is stable between renders and between orgs — the members list arrives
 * in whatever order the API returns, and a picker that reshuffles as you use it is unusable.
 */
export function membersAssignableToRole(members: OrgMember[], role: OrgRole): OrgMember[] {
  return members
    .filter((member) => isActiveMemberStatus(member.status) && member.role_id !== role.id)
    .sort((a, b) => a.email.localeCompare(b.email));
}

/**
 * Whether a status string is the active one.
 *
 * A predicate over the status string rather than over a member, because the two callers hold
 * different things: the picker filters whole members, while the row-level Role control has only the
 * status. Both must agree — `updateOrgUserRole` requires an ACTIVE member and rejects anything
 * else, so a control offered on a deactivated seat is a control that cannot work.
 *
 * `toLowerCase` because the status arrives from two sources with different casing conventions: the
 * REST members list sends lowercase, the GraphQL mutations return the enum uppercase.
 */
export function isActiveMemberStatus(status: string): boolean {
  return status.toLowerCase() === 'active';
}

/**
 * Members who currently hold owner authority.
 *
 * Counted from `management_level`, which the API derives from the assigned role — not from the role
 * list, because several members can hold the one Owner role simultaneously.
 */
export function ownerCount(members: OrgMember[]): number {
  return members.filter(
    (member) => isActiveMemberStatus(member.status) && member.management_level === 'owner',
  ).length;
}

/**
 * Why this member's role must not be changed, or `undefined` when it may be.
 *
 * THE HAZARD, precisely. An organization has exactly ONE owner-level role — the backend refuses to
 * mint a second ("Cannot create another owner role", blocked for everyone). So every role a sole
 * owner could be moved to carries admin or member authority, and moving them:
 *
 *   1. leaves the organization with no owner at all, and owner is the only level that may create or
 *      grant admin roles — so nobody can ever restore it from inside the product; and
 *   2. immediately locks that person out of this screen, since both the panel's authority
 *      (`canManage`) and the sidebar link require owner or admin.
 *
 * Together that is unrecoverable, and the documented failures for the member-role endpoint are only
 * "member not found" and "role not found in this organization" — no last-owner guard. So this is
 * gated here rather than left to a 422 that the contract does not promise.
 *
 * NOT gated: an owner stepping down while ANOTHER owner remains, and an admin assigning themselves a
 * member-level role. Both cost the actor their own access, but another owner can undo them, so they
 * are recoverable and a hard block would be the wrong trade.
 */
export function roleChangeBlockedReason(
  member: OrgMember,
  members: OrgMember[],
): string | undefined {
  if (member.management_level !== 'owner') return undefined;
  if (ownerCount(members) > 1) return undefined;

  const otherActive = members.filter(
    (candidate) => isActiveMemberStatus(candidate.status) && candidate.user_id !== member.user_id,
  ).length;

  // Two different dead ends, and the difference is the whole point: one has a next step, the other
  // has none. Telling someone to "give another member the Owner role" when they are the only member
  // is advice they cannot act on.
  return otherActive === 0
    ? 'The only member of this connection must stay its owner — there is nobody to hand ownership to'
    : 'The organization must keep an owner. Give another member the Owner role first';
}
